/**
 * Print-tilstand — "er siden ved at blive lagt på papir/PDF lige nu?"
 *
 * HVORFOR (Charlie 19-08-2026, 12-conventions §27): på skærmen er "I dag" og
 * "2 timer siden" den mest læsbare form, fordi læseren VED hvornår hun kigger.
 * I en PDF eller en Excel-fil er de værdiløse: dokumentet åbnes måske om en
 * måned, og så kan ingen se hvilken dato der er tale om. Derfor skifter
 * dato-hjælperne i `datetime.ts` til den konkrete dato, mens flaget er sat.
 *
 * Flaget er en `ref`, så alle skabeloner der kalder formatterne gentegner når
 * det skifter — det er netop dét der gør at PDF'en indeholder datoerne og
 * skærmen bagefter er tilbage til "I dag".
 *
 * To indgange, fordi der er to måder at printe på:
 *   - `installPrintMode()` (App-start): browserens egen udskrift (Ctrl+P).
 *   - `printWithAbsoluteDates()`: appens PDF-knapper — her SKAL vi vente på at
 *     Vue har tegnet (nextTick) FØR `window.print()`, fordi print() blokerer
 *     og ellers ville fotografere siden inden gentegningen.
 */

import { nextTick, ref } from 'vue';

/** Sand mens siden gøres klar til print/PDF. Læses af `datetime.ts`. */
export const isPrinting = ref(false);

/** Browserens egen udskrift (Ctrl+P, menuen) — registreres én gang ved app-start. */
export function installPrintMode(): void {
  window.addEventListener('beforeprint', () => {
    isPrinting.value = true;
  });
  window.addEventListener('afterprint', () => {
    isPrinting.value = false;
  });
}

/**
 * Kør en print-handling med absolutte datoer. `print` kaldes FØRST når Vue har
 * gentegnet med flaget sat; flaget ryddes igen bagefter (afterprint rydder det
 * også, hvis browseren fyrer den først).
 */
export async function printWithAbsoluteDates(print: () => void): Promise<void> {
  isPrinting.value = true;
  await nextTick();
  try {
    print();
  } finally {
    isPrinting.value = false;
  }
}
